import { useEffect } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut as fbSignOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authApi } from '../lib/api/auth';
import { unregisterFcmToken } from '../lib/fcm';
import { clearUserScopedState } from '../lib/sign-out-cleanup';
import { useAuthStore } from '../stores/auth.store';

/**
 * One-time subscription to Firebase Auth state. Mount once at the top of
 * the React tree (App.tsx) — NOT in components that render conditionally,
 * or we'll end up with duplicate listeners.
 */
export function useAuthBootstrap(): void {
  const setFirebaseUser = useAuthStore((s) => s.setFirebaseUser);
  const setProfile = useAuthStore((s) => s.setProfile);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (user) {
        try {
          const me = await authApi.me();
          setProfile(me);
          // Web-push registration is owned by <FcmTenantSync> (fires once
          // the resolved city is known and re-registers on change) — a
          // second no-tenant register() here would race it and could wipe
          // the device's city/topic membership. Single registrar only.
        } catch {
          // /auth/me failure isn't fatal — the Firebase user is signed in,
          // we just couldn't refine the profile. UI shows minimal info.
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    });
    return unsub;
  }, [setFirebaseUser, setProfile]);
}

/**
 * Imperative auth actions used by the welcome / verify screens.
 */
export function useAuthActions(): {
  signInWithToken: (customToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
} {
  return {
    async signInWithToken(customToken: string): Promise<void> {
      await signInWithCustomToken(auth, customToken);
      // onAuthStateChanged in useAuthBootstrap picks up the change and
      // refreshes the store + profile.
    },
    async signOut(): Promise<void> {
      // Drop the FCM token before Firebase signs out so the api stops
      // pushing to a device that no longer represents this user.
      await unregisterFcmToken();
      await fbSignOut(auth);
      // Leave no trace of the signed-out account on this device — see
      // clearUserScopedState for exactly what is purged and what is kept.
      await clearUserScopedState();
    },
    /**
     * Re-fetch /auth/me and update the store — used after actions that
     * change the profile server-side (avatar upload/remove) so the UI
     * reflects the new state without a full reload.
     */
    async refreshProfile(): Promise<void> {
      const me = await authApi.me();
      useAuthStore.getState().setProfile(me);
    },
  };
}
